import {Button, Dialog} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRef, useState} from 'react'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import type {StackData} from '../page-data/payloads/header'

type UnstackButtonProps = {
  stack: StackData | undefined | null
  setIsEditing?: (isEditing: boolean) => void
}

// Removes ALL pull requests from the stack, not just the current pull request.
export function UnstackButton({setIsEditing, stack}: UnstackButtonProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isPullRequestStacksEnabled = useFeatureFlag('pull_request_stacks')

  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')

  const queryClient = useQueryClient()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()

  const unstackButtonRef = useRef<HTMLButtonElement>(null)

  const onRequestUnstack = () => {
    setIsConfirmingUnstack(true)
  }

  const onCancelUnstack = () => {
    setIsConfirmingUnstack(false)
    setErrorMessage('')
  }

  const handleUnstackError = (message: string) => {
    setErrorMessage(message)
  }

  const onConfirmUnstack = () => {
    if (!stack) return

    setErrorMessage('')
    mutateUnstackPullRequests(
      {stackId: stack.id},
      {
        onError: error => handleUnstackError(error.message),
        onSuccess: async () => {
          try {
            // We're using refetchQueries here instead of invalidateQueries in order to ensure that the
            // page data is updated *before* the confirmation dialog is closed, for a smoother overall UX.
            await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
            setIsEditing?.(false)
            setIsConfirmingUnstack(false)
          } catch {
            handleUnstackError('Something unexpected happened. Please refresh the page.')
          }
        },
      },
    )
  }

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const isInactive = stack.pulls.some(pull => !pull.isUnstackable)

  return (
    <>
      <Button ref={unstackButtonRef} onClick={onRequestUnstack} size="small" inactive={isInactive}>
        Unstack
      </Button>
      <UnstackDialog
        unstackButtonRef={unstackButtonRef}
        onCancelUnstack={onCancelUnstack}
        onConfirmUnstack={onConfirmUnstack}
        isConfirmingUnstack={isConfirmingUnstack}
        isInactive={isInactive}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </>
  )
}

type UnstackDialogProps = {
  errorMessage: string
  isConfirmingUnstack: boolean
  isInactive: boolean
  isLoading: boolean
  onCancelUnstack: () => void
  onConfirmUnstack: () => void
  unstackButtonRef: React.RefObject<HTMLButtonElement | null>
}

function UnstackDialog({
  unstackButtonRef,
  onCancelUnstack,
  onConfirmUnstack,
  isConfirmingUnstack,
  isInactive,
  isLoading,
  errorMessage,
}: UnstackDialogProps) {
  if (!isConfirmingUnstack) {
    return null
  }

  if (isInactive) {
    return (
      <Dialog title="Unstacking not allowed" returnFocusRef={unstackButtonRef} onClose={onCancelUnstack}>
        One or more pull requests in the stack are queued for merge. Remove from queue and try again.
      </Dialog>
    )
  }

  return (
    <Dialog
      title="Are you sure you want to unstack these pull requests?"
      returnFocusRef={unstackButtonRef}
      onClose={onCancelUnstack}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onCancelUnstack},
        {
          buttonType: 'primary',
          content: 'Confirm',
          onClick: onConfirmUnstack,
          // aria-disabled must be not set when false to avoid broken styling
          'aria-disabled': isLoading ? 'true' : undefined,
          disabled: isLoading,
          loading: isLoading,
        },
      ]}
    >
      All pull requests will be removed from the stack. Their base branches will not be retargeted and their commit
      histories will remain the same.
      {errorMessage && (
        <InlineMessage className="mt-2" variant="critical">
          {errorMessage}
        </InlineMessage>
      )}
      {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
      <span className="sr-only" aria-live="polite">
        {isLoading ? 'Unstacking pull requests in progress.' : ''}
      </span>
    </Dialog>
  )
}
